/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

const TRIGGER = 72; // px pulled before a release refreshes
const MAX = 110; // px cap on the rubber-band
const RESIST = 0.5; // drag resistance

interface PullToRefreshProps {
  /** Runs on release past the threshold; the spinner shows until it settles. */
  onRefresh: () => Promise<unknown> | void;
  children: React.ReactNode;
}

/**
 * Pull-to-refresh for the page (window-scrolled). Touch-only and mobile-only;
 * on ≥ sm it renders children untouched. Engages only when the page is already
 * scrolled to the top, so it never fights normal vertical scrolling.
 */
export const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, children }) => {
  const desktop = useIsDesktop();
  const [pull, setPull] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const start = React.useRef<number | null>(null);

  if (desktop) return <>{children}</>;

  const onTouchStart = (e: React.TouchEvent) => {
    if (refreshing) return;
    if (window.scrollY > 0) return;
    if (e.touches.length !== 1) return;
    start.current = e.touches[0].clientY;
    setDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (start.current === null || refreshing) return;
    // If the user scrolled the page down mid-gesture, abandon the pull.
    if (window.scrollY > 0) {
      start.current = null;
      setDragging(false);
      setPull(0);
      return;
    }
    const dy = e.touches[0].clientY - start.current;
    if (dy <= 0) {
      setPull(0);
      return;
    }
    setPull(Math.min(dy * RESIST, MAX));
  };

  const end = () => {
    if (start.current === null) return;
    start.current = null;
    setDragging(false);
    if (pull >= TRIGGER && !refreshing) {
      setRefreshing(true);
      setPull(TRIGGER);
      Promise.resolve(onRefresh())
        // keep the spinner visible briefly so the refresh reads as deliberate
        .then(() => new Promise((r) => setTimeout(r, 500)))
        .finally(() => {
          setRefreshing(false);
          setPull(0);
        });
    } else {
      setPull(0);
    }
  };

  const progress = Math.min(pull / TRIGGER, 1);
  const armed = pull >= TRIGGER;

  return (
    <div
      className="relative"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={end}
      onTouchCancel={end}
    >
      <div
        className="pointer-events-none absolute left-1/2 top-0 z-10 flex -translate-x-1/2 items-center justify-center"
        style={{
          height: 36,
          width: 36,
          transform: `translate(-50%, ${pull - 36}px)`,
          opacity: pull > 4 ? 1 : 0,
          transition: dragging ? "none" : "transform 0.2s ease, opacity 0.2s ease",
        }}
      >
        <span
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-900",
            armed && "text-blue-600 dark:text-blue-400"
          )}
        >
          <ArrowPathIcon
            className={cn(
              "h-4 w-4 text-gray-500 dark:text-gray-400",
              armed && "text-current",
              refreshing && "animate-spin"
            )}
            style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }}
          />
        </span>
      </div>

      <div
        style={{
          transform: pull > 0 ? `translateY(${pull}px)` : undefined,
          transition: dragging ? "none" : "transform 0.2s ease",
        }}
      >
        {children}
      </div>
    </div>
  );
};
